import { useCallback, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useLanguage } from '../../i18n';
import { usePrefersReducedMotion } from '../../hooks/useMediaQuery';
import { homeLogoBlanco } from '../../assets/images/logo';
import { HERO_VIDEOS } from '../../assets/home';
import './Hero.scss';

const Hero = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const reduceMotion = usePrefersReducedMotion();
  const [videoIndex, setVideoIndex] = useState(0);

  useEffect(() => {
    if (reduceMotion) {
      gsap.set(['.hero__logo', '.hero__subtitle'], { opacity: 1, y: 0, scale: 1 });
      return undefined;
    }

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.5 });
      tl.fromTo(
        '.hero__logo',
        { opacity: 0, scale: 0.8, y: 50 },
        { opacity: 1, scale: 1, y: 0, duration: 1.5, ease: 'power4.out' },
      ).fromTo(
        '.hero__subtitle',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: 'power3.out' },
        '-=0.8',
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [reduceMotion]);

  // Load & (optionally) play the current video whenever the index changes.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.load();
    if (reduceMotion) {
      video.pause();
      return;
    }
    video.play?.().catch(() => {
      /* Autoplay may be blocked by the browser; safe to ignore. */
    });
  }, [videoIndex, reduceMotion]);

  const handleEnded = useCallback(() => {
    setVideoIndex((prev) => (prev + 1) % HERO_VIDEOS.length);
  }, []);

  return (
    <section className="hero" id="hero" ref={sectionRef}>
      <div className="hero__background">
        <video
          ref={videoRef}
          className="hero__video"
          src={HERO_VIDEOS[videoIndex]}
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={handleEnded}
          aria-hidden="true"
        />
        <div className="hero__overlay" />
      </div>

      <div className="hero__content container">
        <div className="hero__text-container">
          <img
            src={homeLogoBlanco}
            alt={t('hero.logoAlt')}
            className="hero__logo"
            fetchPriority="high"
            decoding="async"
          />
          <p className="hero__subtitle">{t('hero.subtitle')}</p>
        </div>
      </div>

      <div className="hero__scroll-indicator" aria-hidden="true">
        <span />
      </div>
    </section>
  );
};

export default Hero;
